import * as React from 'react';
import { useSelector } from 'react-redux';

import { useMessagesRead, useMessagesScroll } from './hooks';
import { ChatScrollLayout } from '../../components/ChatScrollLayout';
import { ChatUnreadMessagesButton } from '../../components/ChatUnreadMessagesButton';
import { ScrollButton } from '../../components/ScrollButton';
import { CHATBOT_ID } from '../../constants/chatbotId';
import { chatbotSelector, currentOpenChatSelector } from '../../selectors';
import { ERequestApiStatus } from '../../types/appState';
import { ChatArchiveInfoContainer } from '../ChatArchiveInfoContainer';
import { ChatMessageMenuProvider, ChatMessageMenuContainer } from '../ChatMessageMenuContainer';
import { ChatPageContainer } from '../ChatPageContainer';
import { ChatRemovalDateContainer } from '../ChatRemovalDateContainer';
import { ChatbotButtonsContainer } from '../ChatbotButtonsContainer';
import { ScrollContainer } from '../ScrollContainer';
import { ChatMessageChatbotTyping } from '../../components/ChatMessageChatbotTyping';

type TProps = {
  scrollRef: React.RefObject<ScrollContainer>;
  scrollToBottom: (isButtonClickScroll?: boolean) => void;
};

export const ChatScrollContainer: React.FC<TProps> = ({ scrollRef, scrollToBottom }) => {
  const [isScrollToBottomButtonVisible, setIsScrollToBottomButtonVisible] = React.useState<boolean>(false);

  const currentChat = useSelector(currentOpenChatSelector);
  const { isTyping } = useSelector(chatbotSelector);

  const isLoading = currentChat?.status === ERequestApiStatus.Pending;

  const { unreadMessagesCount, setRefInMessagesMap, markMessagesAsRead, getFirstUnreadMessage } = useMessagesRead();
  const { getNextMessagesPage } = useMessagesScroll(scrollToBottom, getFirstUnreadMessage);

  const lastPageMessagesLength = React.useMemo(() => {
    if (currentChat?.pages && currentChat.pages.length) {
      const lastPageMessages = currentChat.pages[currentChat.pages.length - 1]?.messages;

      return lastPageMessages?.length;
    }

    return 0;
  }, [currentChat?.pages]);

  React.useEffect(() => {
    markMessagesAsRead();
  }, [markMessagesAsRead, lastPageMessagesLength, currentChat]);

  const handlePageScroll = React.useCallback(
    (scrollTop: number) => {
      if (currentChat?.chatId === CHATBOT_ID) {
        return;
      }

      getNextMessagesPage(scrollTop);
    },
    [getNextMessagesPage, currentChat],
  );

  const pagesNode = React.useMemo(() => {
    const nodes = currentChat?.pages.map(page => (
      <ChatPageContainer key={page.date} page={page} setRefInMessagesMap={setRefInMessagesMap} />
    ));

    if (nodes && isTyping) {
      nodes.push(<ChatMessageChatbotTyping key="typing-message" />);
    }

    return nodes;
  }, [currentChat?.pages, setRefInMessagesMap, isTyping]);

  return (
    <ChatMessageMenuProvider>
      <ScrollContainer
        ref={scrollRef}
        showScrollBottomButton={true}
        onButtonVisible={setIsScrollToBottomButtonVisible}
        onScroll={handlePageScroll}
      >
        <ChatScrollLayout
          isLoading={isLoading}
          pagesNode={pagesNode}
          chatbotButtonsNode={<ChatbotButtonsContainer />}
          removalDateNode={<ChatRemovalDateContainer />}
          archiveInfoNode={<ChatArchiveInfoContainer />}
        />
      </ScrollContainer>
      {unreadMessagesCount > 0 && (
        <ChatUnreadMessagesButton counter={unreadMessagesCount} onClick={() => scrollToBottom(true)} />
      )}
      {isScrollToBottomButtonVisible && <ScrollButton onClick={() => scrollToBottom(true)} />}
      <ChatMessageMenuContainer />
    </ChatMessageMenuProvider>
  );
};
